<template>
  <div
    class="overlay"
    @click.self="$emit('fechar')"
  >
    <div class="modal-filtros">
      <div class="modal-header">
        <span>Filtros Atividades</span>
        <button
          class="btn-fechar"
          @click="$emit('fechar')"
        >
          ✕
        </button>
      </div>

      <div class="modal-corpo">
        <!-- Unidade da UFC -->
        <div class="coluna">
          <p class="coluna-titulo">Unidade da UFC:</p>
          <FiltroSelect
            v-model="unidadeSelecionada"
            :items="unidades"
            msgTodos="Todas as unidades"
          />
        </div>

        <!-- Categoria -->
        <div class="coluna">
          <p class="coluna-titulo">Categoria:</p>
          <FiltroSelect
            v-model="categoriaSelecionada"
            :items="listaCategorias"
            msgTodos="Todas as categorias"
          />
        </div>

        <!-- Tipo -->
        <div class="coluna">
          <p class="coluna-titulo">Tipo:</p>
          <FiltroSelect
            v-model="tipoSelecionado"
            :items="listaTipos"
            msgTodos="Todos os tipos"
          />
        </div>

        <!-- Modalidade -->
        <div class="coluna">
          <p class="coluna-titulo">Modalidade:</p>
          <FiltroSelect
            v-model="modalidadeSelecionada"
            :items="listaModalidades"
            msgTodos="Todas as modalidades"
          />
        </div>

        <!-- Status -->
        <div class="coluna">
          <p class="coluna-titulo">Status:</p>
          <FiltroSelect
            v-model="statusSelecionado"
            :items="listaStatus"
            msgTodos="Todos os status"
          />
        </div>
      </div>

      <div class="modal-rodape">
        <button
          class="btn-limpar"
          @click="limparFiltros"
        >
          Limpar filtros
        </button>
        <button
          class="btn-filtrar"
          @click="aplicarFiltros"
        >
          Filtrar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import FiltroSelect from "@/components/common/FiltroSelect.vue";
import atividadeService from "@/service/atividadeService";
import unidadesService from "@/service/unidadesService";
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";

const props = defineProps({
  filtrosIniciais: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["fechar", "aplicarFiltros"]);

const router = useRouter();
const route = useRoute();

const auth = useAuthStore();
const { isAutenticado, estaRenovando } = storeToRefs(auth);
const isLogado = computed(() => isAutenticado.value || estaRenovando.value);

const unidades = ref([]);
const listaTipos = ref([]);

const listaCategorias = computed(() => {
  const opcoes = [
    { id: "ATIVIDADE_LIVRE", nome: "Atividades Livres" },
    { id: "ATIVIDADE_INSCRICAO", nome: "Com Inscrição" },
  ];

  if (isLogado.value) {
    opcoes.unshift({ id: "MINHA_ATIVIDADE", nome: "Minhas Atividades" });
  }

  return opcoes;
});

const listaModalidades = [
  { id: "PRESENCIAL", nome: "Presencial" },
  { id: "REMOTA", nome: "Online" },
  { id: "HIBRIDA", nome: "Híbrida" },
];

const listaStatus = [
  { id: "PROGRAMADA", nome: "Programada" },
  { id: "INSCRICOES_ABERTAS", nome: "Inscrições Abertas" },
  { id: "CANCELADA", nome: "Cancelada" },
  { id: "ENCERRADA", nome: "Encerrada" },
  { id: "EM_EXECUCAO", nome: "Em Execução" },
];

function obterValorInicial(campo) {
  const queryVal = route.query[campo];
  if (queryVal) {
    const arr = Array.isArray(queryVal) ? queryVal : queryVal.split(",");
    return arr[0] || "";
  }
  const propVal = props.filtrosIniciais?.[campo];
  if (propVal) {
    if (Array.isArray(propVal)) return propVal[0] || "";
    return propVal;
  }
  return "";
}

const unidadeSelecionada = ref(
  route.query.unidadeId || props.filtrosIniciais?.unidadeId || ""
);
const categoriaSelecionada = ref(obterValorInicial("categoria"));
const tipoSelecionado = ref(
  obterValorInicial("tipo") ? Number(obterValorInicial("tipo")) : ""
);
const modalidadeSelecionada = ref(obterValorInicial("modalidade"));
const statusSelecionado = ref(obterValorInicial("status"));

function sincronizarComQuery(q) {
  unidadeSelecionada.value = q.unidadeId || "";
  categoriaSelecionada.value = q.categoria ? q.categoria.split(",")[0] : "";
  tipoSelecionado.value = q.tipo ? Number(q.tipo.split(",")[0]) : "";
  modalidadeSelecionada.value = q.modalidade ? q.modalidade.split(",")[0] : "";
  statusSelecionado.value = q.status ? q.status.split(",")[0] : "";
}

watch(
  () => route.query,
  (newQuery) => {
    sincronizarComQuery(newQuery);
  },
  { deep: true }
);

onMounted(async () => {
  try {
    const [resTipos, resUnidades] = await Promise.all([
      atividadeService.getTiposeSubtipos(),
      unidadesService.getAllUnidades(0, 1000),
    ]);
    if (resTipos && resTipos.length > 0) {
      listaTipos.value = resTipos.map((t) => ({
        id: t.id,
        nome: t.nome,
      }));
    }
    if (resUnidades && Array.isArray(resUnidades)) {
      unidades.value = resUnidades;
    }
  } catch (e) {
    console.error("Erro ao carregar dados em FiltroAtividade:", e);
  }

  sincronizarComQuery(route.query);
});

function limparFiltros() {
  const query = { ...route.query };
  delete query.unidadeId;
  delete query.categoria;
  delete query.tipo;
  delete query.modalidade;
  delete query.status;
  delete query.page;

  unidadeSelecionada.value = "";
  categoriaSelecionada.value = "";
  tipoSelecionado.value = "";
  modalidadeSelecionada.value = "";
  statusSelecionado.value = "";

  const resultado = {
    categoria: [],
    tipo: [],
    modalidade: [],
    status: [],
    unidadeId: null,
  };

  router.push({ query });
  emit("aplicarFiltros", resultado);
  emit("fechar");
}

function aplicarFiltros() {
  const query = { ...route.query };

  if (unidadeSelecionada.value) {
    query.unidadeId = unidadeSelecionada.value;
  } else {
    delete query.unidadeId;
  }

  if (categoriaSelecionada.value) {
    query.categoria = categoriaSelecionada.value;
  } else {
    delete query.categoria;
  }

  if (tipoSelecionado.value) {
    query.tipo = tipoSelecionado.value;
  } else {
    delete query.tipo;
  }

  if (modalidadeSelecionada.value) {
    query.modalidade = modalidadeSelecionada.value;
  } else {
    delete query.modalidade;
  }

  if (statusSelecionado.value) {
    query.status = statusSelecionado.value;
  } else {
    delete query.status;
  }

  delete query.page;

  const resultado = {
    categoria: categoriaSelecionada.value ? [categoriaSelecionada.value] : [],
    tipo: tipoSelecionado.value ? [tipoSelecionado.value] : [],
    modalidade: modalidadeSelecionada.value
      ? [modalidadeSelecionada.value]
      : [],
    status: statusSelecionado.value ? [statusSelecionado.value] : [],
    unidadeId: unidadeSelecionada.value || null,
  };

  router.push({ query });
  emit("aplicarFiltros", resultado);
  emit("fechar");
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-filtros {
  background: var(--branco);
  border-radius: 12px;
  width: 420px;
  max-width: 95vw;
  overflow: hidden;
  box-shadow: 0 8px 32px var(--preto);
}

.modal-header {
  background: var(--roxo-escuro2);
  color: var(--branco);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 14px 20px;
  font-size: 1rem;
  font-weight: 600;
  position: relative;
}

.btn-fechar {
  position: absolute;
  right: 14px;
  background: transparent;
  border: 2px solid var(--branco);
  color: var(--branco);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-corpo {
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: var(--branco);
  gap: 16px;
}

.coluna {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.coluna-titulo {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--preto);
  margin: 0;
}

.modal-rodape {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 16px 20px 24px;
  background: var(--branco);
}

.btn-limpar {
  border: 2px solid var(--roxo-claro);
  background: var(--branco);
  color: var(--roxo-claro);
  border-radius: 10px;
  padding: 8px 24px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-limpar:hover {
  background: #f8f6fc;
}

.btn-filtrar {
  background: var(--roxo-claro);
  color: var(--branco);
  border: none;
  border-radius: 10px;
  padding: 8px 24px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-filtrar:hover {
  opacity: 0.9;
}
</style>
